import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAgent, updateAgent } from '../../api/agents';
import {
  createSubAgent,
  deleteSubAgent,
  getSubAgent,
  listSubAgents,
  updateSubAgent,
} from '../../api/subAgents';
import { getApiErrorMessage } from '../../utils/errors';
import { buildSubAgentPayload } from '../Onboarding/constants';
import AgentSetup from '../Onboarding/steps/AgentSetup';
import {
  buildSubAgentUpdatePayload,
  isPersistedSubAgent,
  mapAgentToForm,
  mapSubAgentFromApi,
} from './formUtils';

function AgentEdit() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const loadedSubAgentIdsRef = useRef([]);

  const [initialValues, setInitialValues] = useState(null);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const loadAgent = useCallback(async () => {
    setIsLoadingInitial(true);
    setLoadError('');

    try {
      const agent = await getAgent(agentId);
      const formValues = mapAgentToForm(agent);

      const listResponse = await listSubAgents(agentId, { page: 1, perPage: 100 });
      const summaries = listResponse.data || [];

      const subAgents = await Promise.all(
        summaries.map((summary) => getSubAgent(summary.id))
      );

      loadedSubAgentIdsRef.current = subAgents.map((item) => item.id);
      formValues.subAgents = subAgents.map(mapSubAgentFromApi);
      setInitialValues(formValues);
    } catch (err) {
      setLoadError(getApiErrorMessage(err, 'Não foi possível carregar o agente.'));
      setInitialValues(null);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  const handleCancel = () => {
    navigate('/agents');
  };

  const syncSubAgents = async (subAgents) => {
    const currentPersistedIds = new Set(
      subAgents.filter((item) => isPersistedSubAgent(item.id)).map((item) => item.id)
    );
    const removedIds = loadedSubAgentIdsRef.current.filter((id) => !currentPersistedIds.has(id));

    const updates = [];
    const creates = [];

    subAgents.forEach((subAgent, index) => {
      if (isPersistedSubAgent(subAgent.id)) {
        updates.push(
          updateSubAgent(subAgent.id, buildSubAgentUpdatePayload(subAgent, index, subAgents))
        );
      } else {
        creates.push(
          createSubAgent(agentId, buildSubAgentPayload(subAgent, index, subAgents))
        );
      }
    });

    await Promise.all(updates);
    await Promise.all(creates);
    await Promise.all(removedIds.map((id) => deleteSubAgent(id)));
  };

  const handleSubmit = async (formData) => {
    setSubmitError('');
    setIsSubmitting(true);

    try {
      await updateAgent(agentId, {
        name: formData.agentName,
        tone_of_voice: formData.toneOfVoice,
        llm_model_line_id: formData.llmModelLineId,
        llm_effort_id: formData.llmEffortId,
        configurations: formData.configurations,
      });

      await syncSubAgents(formData.subAgents);

      navigate('/agents', {
        replace: true,
        state: { editSuccess: true, agentName: formData.agentName },
      });
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, 'Não foi possível salvar o agente.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-red-700">{loadError}</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => navigate('/agents')} className="btn-secondary text-sm px-3 py-2">
              Voltar
            </button>
            <button type="button" onClick={loadAgent} className="btn-primary text-sm px-3 py-2">
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AgentSetup
      mode="edit"
      stepNumber={1}
      totalSteps={1}
      hasKnowledge={false}
      initialValues={initialValues}
      isLoadingInitial={isLoadingInitial}
      persistedAgentId={agentId}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      submitError={submitError}
    />
  );
}

export default AgentEdit;
